import { ingredientsSearchFilters, ingredientsSearchTabs } from 'constants/ingredientsSearch';
import TypeItem from 'records/TypeItem';
import ImagePlaceholder from 'assets/img/menu-item-placeholder.jpg';
import { operatingCountries } from 'constants/user';
import { getAbsoluteSupc } from 'helpers/ingredientHelper';
import DefaultImage from 'assets/img/menu-item-placeholder.jpg';

/**
 * Generate table data products list from given store products
 * Apply filters and search
 * @param {List} products
 * @param {Object} filters
 * @param {String} tab tabKey
 * @param {Object} searchProps search properties eg: {keyword:'text',isSupc:false,searchedTabs: []}
 * @param {*} unitTypes 
 * @param {*} isBatchRecipe 
 * @param {*} currRecipeId 
 */
export const generateTableData = (products, filters, tab, searchProps, unitTypes, isBatchRecipe, currRecipeId) => {
  const filteredUnavailableProducts = filterUnavailableProducts(products);
  const searchedProducts = searchTab(searchProps, tab, filteredUnavailableProducts);
  const filteredProducts = filterProducts(searchedProducts, filters, tab, isBatchRecipe, currRecipeId);
  const tableData = filteredProducts.map((product, index) => {
    const {
      id,
      name,
      ingredientName, // previously used
      recipeName, // batch recipe
      note,
      supc,
      brand,
      ingredientBrand, // previously used
      packSizeSize,
      packSizePack,
      costPerServing, // batch recipes
      costPerPortion, // batch recipes
      yieldUnits, // batch recipes
      timesUsed, // batch recipes
      usedInRecipes, // batch recipes
      contentFileList, // batch recipes
      isSyscoBrand,
      packSize, // itemList
      pack, // itemList
      priceStatuses,
    } = product;

    const amountUnitTextKey = yieldUnits ?
      TypeItem.findTypeItemById(unitTypes, yieldUnits).abbreviation : null;
    const supcWithoutOpco = supc ? getAbsoluteSupc(supc) : null;

    return {
      id,
      key: index,
      name: generateProductName(tab, name, ingredientName, recipeName, note),
      supc: supcWithoutOpco,
      brand: brand || ingredientBrand,
      packSize: generatePackSize(tab, packSizePack, packSize, packSizeSize, pack),
      cost: generateProductCost(tab, product, amountUnitTextKey),
      totalCost: costPerServing,
      yield: `${product.yield} ${amountUnitTextKey}`,
      image: generateProductImage(tab, supcWithoutOpco, contentFileList),
      timesUsed,
      usedInRecipes: usedInRecipes || [],
      amountUnitTextKey,
      costPerPortion,
      isSyscoBrand: !!isSyscoBrand,
      product,
      priceStatuses: priceStatuses,
    }
  });

  return tableData;
};

/**
 * Returns relevant product name according to the tab
 * @param {*} tab 
 * @param {*} name 
 * @param {*} ingredientName 
 * @param {*} recipeName 
 * @param {*} note 
 */
const generateProductName = (tab, name, ingredientName, recipeName, note) => {
  switch (tab) {
    case ingredientsSearchTabs.PREVIOUSLY_USED_TAB.key:
    case ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key:
      return ingredientName;
    case ingredientsSearchTabs.BATCH_RECIPES_TAB.key:
      return recipeName || note;
    case ingredientsSearchTabs.ITEM_LISTS_TAB.key:
      return ingredientName;
    default:
      return note || name;
  }
};
/**
 * since each tabs packsize and pack fetch in different names
 * @param {*} tab 
 * @param {*} packSizePack 
 * @param {*} packSize // item lists
 * @param {*} packSizeSize 
 * @param {*} pack // item lists
 */
const generatePackSize = (tab, packSizePack, packSize, packSizeSize, pack) => {
  if (tab === ingredientsSearchTabs.ITEM_LISTS_TAB.key) {
    return `${pack}/${packSize}`;
  }
  return `${packSizePack}/${packSizeSize}`;
};

/**
 * Returns relevant formatted product cost according to the tab
 * @param {*} tab
 * @param {*} product
 * @param {*} amountUnitTextKey
 */
const generateProductCost = (tab, product, amountUnitTextKey) => {
  const {
    priceLoaded,
    priceLoadSuccess,
    unitCost,
    isCatchWeight,
    isSoldAs_split,
    isSoldAs_case,
    costPerUnit,
    baseUnitTextKey,
    costPerPortion,
  } = product;

  let cost = '...';
  const unit = isCatchWeight ? 'LB' : (isSoldAs_split && !isSoldAs_case ? 'EA' : 'CS');

  switch (tab) {
    case ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key:
      return `$${costPerUnit}/${baseUnitTextKey}`;
    case ingredientsSearchTabs.BATCH_RECIPES_TAB.key:
      return `$${costPerPortion}/${amountUnitTextKey}`;
    case ingredientsSearchTabs.PREVIOUSLY_USED_TAB.key:
      cost = `$${Number(unitCost).toFixed(3)} ${unit.toLowerCase()}.`;
      return cost;
    default:
      if (priceLoaded) {
        cost = priceLoadSuccess ? `$${Number(unitCost).toFixed(3)} ${unit.toLowerCase()}.` : 'N/A';
      }
      return cost;
  }
};

export const formatPrice = (price) => {
  if (price !== "" && !isNaN(price)) {
      return `$${ Number(price).toFixed(3) }`;
  }
    return '--';
};

/**
 * Returns relevant formatted product image url according to the tab
 * @param {*} tab 
 * @param {*} supc 
 * @param {*} contentFileList 
 */
export const generateProductImage = (tab, supc, contentFileList) => {
  if (tab === ingredientsSearchTabs.BATCH_RECIPES_TAB.key) {
    const image = contentFileList && contentFileList[0] && contentFileList[0].contentUrl ?
      contentFileList && contentFileList[0] && contentFileList[0].contentUrl :
      ImagePlaceholder;
    return image;
  }

  return `https://image.sysco.com/image-server/product/image/${supc}/thumbnail/1.png`;
};

/**
 * Handle image render error
 * @param {Error} e error
 * @param {Image} replacementImage replacing image
 */
export const handleImageError = (e, replacementImage) => {
  e.target.onerror = null;
  e.target.src = replacementImage ? replacementImage : DefaultImage;
};

/**
 * Filter table data products list with given filters
 * If batch recipe and batch recipes tab, filter out the current recipe
 * @param {List} products list of products
 * @param {Object} filters
 * @param {String} tab tabKey
 * @param {Boolean} isBatchRecipe is recipe a batch recipe
 * @param {*} recipeId recipe Id (for batch recipes)
 */
const filterProducts = (products, filters, tab, isBatchRecipe, currRecipeId) => {
  switch (tab) {
    case ingredientsSearchTabs.BATCH_RECIPES_TAB.key:
      // when in a batch recipe,
      // ingredients search batch recipes tab should not show the same batch recipe
      return isBatchRecipe ? products.filter(product => product.id !== currRecipeId) : products;
    case ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key:
      return products.filter(product => checkProductSelected(filters, ingredientsSearchFilters.BRAND.id, product.ingredientBrand))
    default:
      return products.filter(product =>
        checkProductSelected(filters, ingredientsSearchFilters.CATEGORY.id, product.mainCategoryId) &&
        checkProductSelected(filters, ingredientsSearchFilters.BRAND.id, product.brand || product.ingredientBrand) &&
        checkProductSelected(filters, ingredientsSearchFilters.PACK_SIZE.id, product.packSizeSize || product.packSize) &&
        checkProductSelected(filters, ingredientsSearchFilters.STOCK_TYPE.id, product.stockFlag || product.stockType));
  }
};

/**
 * Check if the product is selected from a given filter section
 * @param {*} filters 
 * @param {*} facetId 
 * @param {*} productFacetValue 
 */
const checkProductSelected = (filters, facetId, productFacetValue) => {
  return !(filters[facetId] &&
    filters[facetId].length > 0 &&
    (filters[facetId].indexOf(productFacetValue) === -1))
}

/**
 * Returns boolean stating whether the passed phrase is numeric
 * @param {String} word phrase to be validated
 * @returns {Boolean} is number
 */
export const isSupc = (word) => {
  if (typeof word != "string" || word.length > 7) {
    return false
  }
  return !isNaN(word);
}

/**
 * Perform search filter to a given tab and a given data
 * @param {String} searchProps search properties {isSupc:false, keyword:'pizza'}
 * @param {String} tab tabKey
 * @param {List} products list of products
 * @returns {List} filtered products
 */
export const searchTab = (searchProps, tab, products) => {
  let { keyword, isSupc: likeSupc } = searchProps || {};
  const searchableGroup = [
    ingredientsSearchTabs.PURCHASE_HISTORY_TAB.key,
    ingredientsSearchTabs.PREVIOUSLY_USED_TAB.key,
    ingredientsSearchTabs.ITEM_LISTS_TAB.key,
    ingredientsSearchTabs.BATCH_RECIPES_TAB.key,
    ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key,
  ];

  // no filtering if tab-name was not identified
  if (!searchableGroup.includes(tab)) {
    return products;
  }
  // keyword check
  if (!keyword) {
    return products;
  }
  // product check
  if (!products || !products.length) {
    return [];
  }

  return searchProducts(products, keyword, getPrimaryNameAttribute(tab), likeSupc);
}

/**
 * Filters matching list of products with a given keyword
 * @param {List} products list of products
 * @param {String} keyword keyword for filter eg: 'chicken' or '1123'
 * @param {String} primaryNameAttribute name attribute in a ingredient object
 * @param {*} likeSupc whether the keyword is validated as an supc
 * @returns {List} filtered ingredients
 */
export const searchProducts = (products, keyword, primaryNameAttribute, likeSupc) => {
  if (!keyword) {
    return products;
  }
  const keywordUpper = keyword.toUpperCase();

  if (likeSupc) {
    return products.filter((ingredient) =>
      (ingredient.supc && ingredient.supc.includes(keywordUpper)) ||
      (ingredient[primaryNameAttribute] && ingredient[primaryNameAttribute].toUpperCase().includes(keywordUpper)) ||
      (ingredient.note && ingredient.note.toUpperCase().includes(keywordUpper)));
  }
  return products.filter((ingredient) =>
    (ingredient[primaryNameAttribute] && ingredient[primaryNameAttribute].toUpperCase().includes(keywordUpper)) ||
    (ingredient.note && ingredient.note.toUpperCase().includes(keywordUpper)));
}

/**
 * Get ingredient primary name attribute name for a given tab
 * @param {String} tab tabName
 * @returns {String} attribute name
 */
export const getPrimaryNameAttribute = tab => {
  switch (tab) {
    // batch recipes
    case ingredientsSearchTabs.BATCH_RECIPES_TAB.key:
      return 'recipeName';
    // custom ingredients
    case ingredientsSearchTabs.CUSTOM_INGREDIENTS_TAB.key:
    case ingredientsSearchTabs.PREVIOUSLY_USED_TAB.key:
    case ingredientsSearchTabs.ITEM_LISTS_TAB.key:
      return 'ingredientName';
    default:
      return 'name';
  }
}

/**
 * prevent default on tab click
 * @param {*} e event
 */
export const preventTabDefault = e => {
  if (e.keyCode === 9) {
    e.preventDefault()
  }
}

/**
 * Filters the allowed tab headers based on the user account country
 * @param {String} userCountry country of the user account eg: 'USBL', 'CAN'
 * @param {String} isIngredientReplacement isIngredientReplacement
 * @returns filtered tab-header list
 */
export const getAllowedTabList = (userCountry = operatingCountries.unitedStates, isIngredientReplcement, isFromNotificationCenter) => {
  const ingredientsSearchTabList = Object.values(ingredientsSearchTabs);
  if (isIngredientReplcement && isFromNotificationCenter) {
    return userCountry === operatingCountries.canada ? ingredientsSearchTabList.filter(tab => tab.isIngredientReplcement & !tab.isCanadaRestricted) :
      ingredientsSearchTabList.filter(tab => tab.isIngredientReplcement);
  }
  else {
    return userCountry === operatingCountries.canada ? ingredientsSearchTabList.filter(tab => !tab.isCanadaRestricted) : ingredientsSearchTabList;
  }
}

/**
 * Filter unavailable products
 * @param {List} tableData list of products
 * @returns {List} available product list
 */
export const filterUnavailableProducts = (tableData) => {
  const data = tableData.filter((item) => {
    return (!item.priceStatuses || item.priceStatuses.length === 0 || (item.priceStatuses[0].code !== 200 && item.priceStatuses[0].code !== 207))
  });
  return data;
}